import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  CrossClientQueryExecution,
  CrossClientQueryMatchDistribution,
  CrossClientQueryPhase,
  CrossClientQueryPhases,
  CrossClientQueryResult,
  CrossClientQueryResultMatches,
  CrossClientQueryResultStatus,
  CrossClientQueryResultStatuses,
  CrossClientResultTab,
} from '../../models/CrossClient'
import { BasePageResponse } from '../models'
import { PageMetadata } from '../../models/PageMetadata'
import { User } from '../../models/User'
import { selectCrossClientActionedClientIds } from './crossClientActions'

type ProgressStatuses = Partial<Record<CrossClientQueryResultStatus, number>>

interface CrossClientResultsSearch {
  clientName: string | null
}

interface CrossClientTabResults {
  pages: Record<number, CrossClientQueryResult[]>
  totalElements: number
  totalPages: number
}

const EMPTY_RESULTS: CrossClientQueryResult[] = []

interface CrossClientResultsState {
  execution: CrossClientQueryExecution | null
  executionLoading: boolean
  selectedTab: CrossClientResultTab
  resultsByTab: Partial<Record<CrossClientResultTab, CrossClientTabResults>>
  matchDistribution: CrossClientQueryMatchDistribution | null
  progressStatuses: ProgressStatuses
  succeededResultsAtLocalResolverStart: number
  finishedResultsAtAiBatchStart: number
  aiBatchStartCaptured: boolean
  search: CrossClientResultsSearch
  selectedResultIds: string[]
  clients: EntityState<User, string>
  fetchedClientIds: Record<string, true>
  teammates: User[]
  clientsLoading: boolean
  teammatesLoading: boolean
  pollingEnabled: boolean
  paging: { page: number; size: number }
}

const crossClientClientsAdapter = createEntityAdapter<User>()

const emptyPageMeta: PageMetadata = { totalElements: 0, totalPages: 0, number: 0 }

const initialState: CrossClientResultsState = {
  execution: null,
  executionLoading: true,
  selectedTab: CrossClientQueryResultMatches.MATCH,
  resultsByTab: {},
  matchDistribution: null,
  progressStatuses: {},
  succeededResultsAtLocalResolverStart: 0,
  finishedResultsAtAiBatchStart: 0,
  aiBatchStartCaptured: false,
  search: { clientName: null },
  selectedResultIds: [],
  clients: crossClientClientsAdapter.getInitialState(),
  fetchedClientIds: {},
  teammates: [],
  clientsLoading: true,
  teammatesLoading: true,
  pollingEnabled: true,
  paging: { page: 1, size: 20 },
}

const AI_ACTIVE_PHASES: CrossClientQueryPhase[] = [CrossClientQueryPhases.AI_QUEUED, CrossClientQueryPhases.AI_PROCESSING]

const applyPhaseSnapshots = (state: CrossClientResultsState, previousPhase: CrossClientQueryPhase | undefined, nextPhase: CrossClientQueryPhase) => {
  if (nextPhase === CrossClientQueryPhases.LOCAL_QUEUED && previousPhase !== CrossClientQueryPhases.LOCAL_QUEUED) {
    state.succeededResultsAtLocalResolverStart = state.progressStatuses[CrossClientQueryResultStatuses.SUCCEEDED] ?? 0
  }
  if (nextPhase === CrossClientQueryPhases.AI_QUEUED && previousPhase !== CrossClientQueryPhases.AI_QUEUED) {
    state.finishedResultsAtAiBatchStart = countFinishedResults(state.progressStatuses)
    state.aiBatchStartCaptured = true
  }
}

const determineFinishedResultsAtAiBatchStart = (state: CrossClientResultsState) => {
  if (state.aiBatchStartCaptured) {
    return
  }
  const execution = state.execution
  if (!execution || !AI_ACTIVE_PHASES.includes(execution.phase) || Object.keys(state.progressStatuses).length === 0) {
    return
  }
  state.finishedResultsAtAiBatchStart = countFinishedResults(state.progressStatuses)
  state.aiBatchStartCaptured = true
}

const countFinishedResults = (statuses: ProgressStatuses): number =>
  (statuses[CrossClientQueryResultStatuses.SUCCEEDED] ?? 0) +
  (statuses[CrossClientQueryResultStatuses.NO_DATA] ?? 0) +
  (statuses[CrossClientQueryResultStatuses.FAILED] ?? 0)

export const crossClientResultsSlice = createSlice({
  name: 'crossClientResults',
  initialState,
  reducers: {
    setCrossClientResultsExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executionLoading = action.payload
    },
    startCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = false
    },
    setCrossClientPageResults: (state, action: PayloadAction<{ tab: CrossClientResultTab; response: BasePageResponse<CrossClientQueryResult[]> }>) => {
      const { tab, response } = action.payload
      const cache = state.resultsByTab[tab] ?? { pages: {}, totalElements: 0, totalPages: 0 }
      cache.pages[response.page.number] = response.data
      cache.totalElements = response.page.totalElements
      cache.totalPages = response.page.totalPages
      state.resultsByTab[tab] = cache
    },
    setCrossClientMatchDistribution: (state, action: PayloadAction<CrossClientQueryMatchDistribution>) => {
      state.matchDistribution = action.payload
    },
    setCrossClientProgressStatuses: (state, action: PayloadAction<ProgressStatuses>) => {
      state.progressStatuses = action.payload
      determineFinishedResultsAtAiBatchStart(state)
    },
    setCrossClientResultsExecution: (state, action: PayloadAction<CrossClientQueryExecution | null>) => {
      const nextExecution = action.payload
      if (nextExecution) {
        applyPhaseSnapshots(state, state.execution?.phase, nextExecution.phase)
      }
      state.execution = nextExecution
      determineFinishedResultsAtAiBatchStart(state)
    },
    setCrossClientResultsExecutionPhase: (state, action: PayloadAction<CrossClientQueryPhase>) => {
      if (state.execution) {
        applyPhaseSnapshots(state, state.execution.phase, action.payload)
        state.execution.phase = action.payload
        determineFinishedResultsAtAiBatchStart(state)
      }
    },
    setCrossClientSelectedTab: (state, action: PayloadAction<CrossClientResultTab>) => {
      state.selectedTab = action.payload
      state.paging.page = 1
      state.selectedResultIds = []
    },
    setCrossClientSearch: (state, action: PayloadAction<CrossClientResultsSearch>) => {
      state.search = action.payload
      state.paging.page = 1
      state.resultsByTab = {}
      state.selectedResultIds = []
    },
    toggleCrossClientResultSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload
      const index = state.selectedResultIds.indexOf(id)
      if (index === -1) {
        state.selectedResultIds.push(id)
      } else {
        state.selectedResultIds.splice(index, 1)
      }
    },
    setCrossClientPageSelection: (state, action: PayloadAction<{ ids: string[]; selected: boolean }>) => {
      const { ids, selected } = action.payload
      if (selected) {
        const merged = new Set(state.selectedResultIds)
        for (const id of ids) {
          merged.add(id)
        }
        state.selectedResultIds = Array.from(merged)
      } else {
        const idSet = new Set(ids)
        state.selectedResultIds = state.selectedResultIds.filter((id) => !idSet.has(id))
      }
    },
    resetCrossClientSelection: (state) => {
      state.selectedResultIds = []
    },
    addCrossClientClients: (state, action: PayloadAction<User[]>) => {
      crossClientClientsAdapter.addMany(state.clients, action.payload)
    },
    addCrossClientFetchedClientIds: (state, action: PayloadAction<string[]>) => {
      state.fetchedClientIds = action.payload.reduce((acc, id) => ({ ...acc, [id]: true }), state.fetchedClientIds)
    },
    removeCrossClientFetchedClientIds: (state, action: PayloadAction<string[]>) => {
      const next = { ...state.fetchedClientIds }
      for (const id of action.payload) {
        delete next[id]
      }
      state.fetchedClientIds = next
    },
    setCrossClientTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    setCrossClientClientsLoading: (state, action: PayloadAction<boolean>) => {
      state.clientsLoading = action.payload
    },
    setCrossClientTeammatesLoading: (state, action: PayloadAction<boolean>) => {
      state.teammatesLoading = action.payload
    },
    setCrossClientPage: (state, action: PayloadAction<number>) => {
      state.paging.page = action.payload
    },
    setCrossClientPageSize: (state, action: PayloadAction<number>) => {
      state.paging.size = action.payload
      state.resultsByTab = {}
    },
    resetCrossClientResults: () => initialState,
  },
})

export const {
  setCrossClientResultsExecutionLoading,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
  setCrossClientPageResults,
  setCrossClientMatchDistribution,
  setCrossClientProgressStatuses,
  setCrossClientResultsExecution,
  setCrossClientResultsExecutionPhase,
  setCrossClientSelectedTab,
  setCrossClientSearch,
  toggleCrossClientResultSelection,
  setCrossClientPageSelection,
  resetCrossClientSelection,
  addCrossClientClients,
  addCrossClientFetchedClientIds,
  removeCrossClientFetchedClientIds,
  setCrossClientTeammates,
  setCrossClientClientsLoading,
  setCrossClientTeammatesLoading,
  setCrossClientPage,
  setCrossClientPageSize,
  resetCrossClientResults,
} = crossClientResultsSlice.actions

const crossClientClientSelectors = crossClientClientsAdapter.getSelectors((state: RootState) => state.crossClientResults.clients)

export const selectCrossClientResultsExecution = (state: RootState) => state.crossClientResults.execution

export const selectCrossClientResultExecutionLoading = (state: RootState) => state.crossClientResults.executionLoading

export const selectCrossClientSelectedTab = (state: RootState) => state.crossClientResults.selectedTab

const selectCurrentTabPage = (state: RootState): CrossClientQueryResult[] | undefined => {
  const { resultsByTab, selectedTab, paging } = state.crossClientResults
  return resultsByTab[selectedTab]?.pages[paging.page - 1]
}

export const selectCrossClientPageResults = (state: RootState) => selectCurrentTabPage(state) ?? EMPTY_RESULTS

export const selectCrossClientCurrentTabLoaded = (state: RootState) => !!selectCurrentTabPage(state)

export const selectCrossClientPageMeta = (state: RootState): PageMetadata => {
  const { resultsByTab, selectedTab, paging } = state.crossClientResults
  const cache = resultsByTab[selectedTab]
  return cache ? { totalElements: cache.totalElements, totalPages: cache.totalPages, number: paging.page - 1 } : emptyPageMeta
}

export const selectCrossClientPaging = (state: RootState) => state.crossClientResults.paging

export const selectCrossClientSearch = (state: RootState) => state.crossClientResults.search

export const selectCrossClientSelectedResultIds = (state: RootState) => state.crossClientResults.selectedResultIds

export const selectCrossClientMatchDistribution = (state: RootState) => state.crossClientResults.matchDistribution

export const selectCrossClientProgressStatuses = (state: RootState) => state.crossClientResults.progressStatuses

export const selectCrossClientSucceededResultsAtLocalResolverStart = (state: RootState) => state.crossClientResults.succeededResultsAtLocalResolverStart

export const selectCrossClientFinishedResultsAtAiBatchStart = (state: RootState) => state.crossClientResults.finishedResultsAtAiBatchStart

export interface CrossClientTabCounts {
  full: number
  partial: number
  noMatch: number
  incomplete: number
}

export const selectCrossClientTabCounts = createSelector(
  [selectCrossClientMatchDistribution, selectCrossClientProgressStatuses],
  (distribution, statuses): CrossClientTabCounts => ({
    full: distribution?.full ?? 0,
    partial: distribution?.partial ?? 0,
    noMatch: distribution?.noMatch ?? 0,
    incomplete: statuses[CrossClientQueryResultStatuses.NO_DATA] ?? 0,
  }),
)

export const selectCrossClientMatchedWithoutActionsCount = createSelector(
  [selectCrossClientTabCounts, selectCrossClientActionedClientIds],
  (counts, actionedClientIds) => Math.max(counts.full - actionedClientIds.size, 0),
)

export const selectCrossClientTeammates = createSelector([(state: RootState) => state.crossClientResults.teammates], (teammates) => {
  return teammates.reduce(
    (teammateMap, teammate) => {
      teammateMap[teammate.id] = teammate
      return teammateMap
    },
    {} as Record<string, User>,
  )
})

export const selectCrossClientClients = createSelector([crossClientClientSelectors.selectAll], (clients) => {
  return clients.reduce(
    (clientMap, client) => {
      clientMap[client.id] = client
      return clientMap
    },
    {} as Record<string, User>,
  )
})

export const selectCrossClientFetchedClientIds = (state: RootState) => state.crossClientResults.fetchedClientIds

export const selectCrossClientClientsLoading = (state: RootState) => state.crossClientResults.clientsLoading

export const selectCrossClientTeammatesLoading = (state: RootState) => state.crossClientResults.teammatesLoading

export const selectCrossClientResultsPollingEnabled = (state: RootState) => state.crossClientResults.pollingEnabled

export const selectShouldPollCrossClientExecutionResults = (state: RootState) => {
  const execution = state.crossClientResults.execution
  if (!execution) {
    return false
  }

  switch (execution.phase) {
    case CrossClientQueryPhases.INITIALIZED:
    case CrossClientQueryPhases.LOCAL_READY:
    case CrossClientQueryPhases.LOCAL_QUEUED:
    case CrossClientQueryPhases.LOCAL_PROCESSING:
    case CrossClientQueryPhases.AI_QUEUED:
    case CrossClientQueryPhases.AI_PROCESSING:
    case CrossClientQueryPhases.CANCELING:
      return true
    default:
      return false
  }
}

export default crossClientResultsSlice.reducer
